import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Row,
  Col,
  Card,
  Table,
  Button,
  Input,
  Select,
  DatePicker,
  Tag,
  Space,
  Modal,
  Form,
  message,
  Typography,
  Statistic,
  Tabs,
  Badge,
  Descriptions,
} from 'antd';
import {
  SearchOutlined,
  ExportOutlined,
  ReloadOutlined,
  EyeOutlined,
  CreditCardOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  RollbackOutlined,
} from '@ant-design/icons';
import { InputNumber } from 'antd';
import { RootState, AppDispatch } from '../store';
import { fetchPaymentIntents, fetchRefunds } from '../store/slices/paymentSlice';
import RefundService from '../services/refunds';
import { PaymentIntent, Refund } from '../types';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

const PaymentsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { paymentIntents, refunds } = useSelector((state: RootState) => state.payment);

  const [activeTab, setActiveTab] = useState('payments');
  const [searchForm] = Form.useForm();

  // 行内退款
  const [refundForm] = Form.useForm();
  const [refundModalVisible, setRefundModalVisible] = useState(false);
  const [refundTarget, setRefundTarget] = useState<PaymentIntent | null>(null);
  const [refunding, setRefunding] = useState(false);

  // 支付详情
  const [detailVisible, setDetailVisible] = useState(false);
  const [detailTarget, setDetailTarget] = useState<PaymentIntent | null>(null);

  useEffect(() => {
    dispatch(fetchPaymentIntents({ page: 1, limit: 20 }));
    dispatch(fetchRefunds({ page: 1, limit: 20 }));
  }, [dispatch]);

  const openRefund = (record: PaymentIntent) => {
    setRefundTarget(record);
    refundForm.setFieldsValue({
      payment_id: record.id,
      amount: ((record.amount || 0) / 100).toFixed(2),
      reason: 'requested_by_customer',
    });
    setRefundModalVisible(true);
  };

  const handleSubmitRefund = async (values: any) => {
    if (!refundTarget) return;
    setRefunding(true);
    try {
      await RefundService.createRefund({
        payment_id: refundTarget.id,
        amount: Math.round(parseFloat(values.amount) * 100),
        reason: values.reason,
      });
      message.success('退款已提交');
      setRefundModalVisible(false);
      setRefundTarget(null);
      refundForm.resetFields();
      dispatch(fetchPaymentIntents({ page: 1, limit: 20 }));
      dispatch(fetchRefunds({ page: 1, limit: 20 }));
    } catch (err: any) {
      const msg = err?.response?.data?.message || '退款失败';
      const detail = err?.response?.data?.error || '';
      // 后端退款以 pm_ 支付记录为准；若交易仍处于支付意图阶段会提示 payment not found
      if (/payment not found/i.test(detail)) {
        message.error('该交易尚未生成可退款的支付记录，请稍后再试');
      } else {
        message.error(msg);
      }
    } finally {
      setRefunding(false);
    }
  };

  const handleSearch = (values: any) => {
    if (activeTab === 'payments') {
      dispatch(fetchPaymentIntents({ page: 1, limit: 20, filters: values }));
    } else {
      dispatch(fetchRefunds({ page: 1, limit: 20, filters: values }));
    }
  };

  const handleReset = () => {
    searchForm.resetFields();
    if (activeTab === 'payments') {
      dispatch(fetchPaymentIntents({ page: 1, limit: 20 }));
    } else {
      dispatch(fetchRefunds({ page: 1, limit: 20 }));
    }
  };

  // 金额格式化为 "10.00 USD"
  const fmtAmount = (amount?: number | null, currency?: string) => {
    const safe = typeof amount === 'number' && !Number.isNaN(amount) ? amount : 0;
    const value = (safe / 100).toFixed(2);
    return currency ? `${value} ${currency.toUpperCase()}` : value;
  };

  const fmtCardBrand = (brand?: string | null): string => {
    if (!brand) return '';
    const map: Record<string, string> = {
      visa: 'Visa',
      mastercard: 'Mastercard',
      amex: 'American Express',
      discover: 'Discover',
      jcb: 'JCB',
      diners: 'Diners Club',
      unionpay: 'UnionPay',
    };
    const key = brand.toLowerCase();
    return map[key] || brand.charAt(0).toUpperCase() + brand.slice(1);
  };

  const fmtFunding = (funding?: string | null): string => {
    if (!funding) return '';
    const map: Record<string, string> = {
      credit: '信用卡',
      debit: '借记卡',
      prepaid: '预付卡',
      unknown: '未知',
    };
    return map[funding.toLowerCase()] || funding;
  };

  const methodTextMap: Record<string, string> = {
    card: '银行卡',
    alipay: '支付宝',
    wechat: '微信支付',
    bank_transfer: '银行转账',
  };

  // 支付方式紧凑展示
  const renderPaymentMethod = (record: PaymentIntent) => {
    const d = record.payment_method_details;
    if (d && d.last4) {
      const brand = fmtCardBrand(d.brand) || methodTextMap[d.type || ''] || '卡';
      return (
        <Space size={4}>
          <CreditCardOutlined style={{ color: '#8c8c8c' }} />
          <span>{brand}</span>
          <span style={{ letterSpacing: 1, color: '#8c8c8c' }}>•••• {d.last4}</span>
        </Space>
      );
    }
    const type = record.payment_method_type || record.payment_method?.type;
    if (type) return <span>{methodTextMap[type] || type}</span>;
    return <span style={{ color: '#bfbfbf' }}>—</span>;
  };

  // 客户信息:receipt_email -> cardholder_name -> customer_id -> —
  const renderCustomer = (record: PaymentIntent) => {
    const email = record.receipt_email || record.customer?.email;
    const holder = record.payment_method_details?.cardholder_name || record.customer?.name;
    const text = email || holder || record.customer_id;
    if (!text) return <Text type="secondary">—</Text>;
    return (
      <div>
        <div>{email || holder || '匿名客户'}</div>
        {email && holder && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {holder}
          </Text>
        )}
      </div>
    );
  };

  const riskConfig: Record<string, { color: string; text: string }> = {
    normal: { color: 'green', text: '正常' },
    elevated: { color: 'orange', text: '偏高' },
    high: { color: 'red', text: '高风险' },
  };

  const openDetail = (record: PaymentIntent) => {
    setDetailTarget(record);
    setDetailVisible(true);
  };

  const statusConfig = {
    'pending': { color: 'processing', text: '待处理', icon: <ClockCircleOutlined /> },
    'processing': { color: 'processing', text: '处理中', icon: <ClockCircleOutlined /> },
    'succeeded': { color: 'success', text: '成功', icon: <CheckCircleOutlined /> },
    'failed': { color: 'error', text: '失败', icon: <CloseCircleOutlined /> },
    'cancelled': { color: 'default', text: '已取消', icon: <CloseCircleOutlined /> },
    'requires_action': { color: 'warning', text: '需要操作', icon: <WarningOutlined /> },
  };

  const paymentColumns = [
    {
      title: '支付ID',
      dataIndex: 'id',
      key: 'id',
      width: 120,
      render: (text: string) => (
        <Button type="link">
          #{text.substring(0, 8)}
        </Button>
      ),
    },
    {
      title: '客户信息',
      key: 'customer_info',
      width: 180,
      render: (_: any, record: PaymentIntent) => renderCustomer(record),
    },
    {
      title: '金额',
      dataIndex: 'amount',
      key: 'amount',
      width: 130,
      render: (amount: number, record: PaymentIntent) => (
        <Text strong>{fmtAmount(amount, record.currency)}</Text>
      ),
    },
    {
      title: '支付方式',
      key: 'payment_method',
      width: 180,
      render: (_: any, record: PaymentIntent) => renderPaymentMethod(record),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => {
        const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
        return (
          <Tag color={config.color} icon={config.icon}>
            {config.text}
          </Tag>
        );
      },
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date: string) => (
        <div>
          <div>{new Date(date).toLocaleDateString()}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {new Date(date).toLocaleTimeString()}
          </Text>
        </div>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      width: 120,
      render: (_: any, record: PaymentIntent) => {
        const refundable = record.status === 'succeeded';
        return (
          <Space>
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => openDetail(record)}
            >
              详情
            </Button>
            <Button
              size="small"
              type="link"
              icon={<RollbackOutlined />}
              disabled={!refundable}
              onClick={() => openRefund(record)}
            >
              退款
            </Button>
          </Space>
        );
      },
    },
  ];

  const refundColumns = [
    {
      title: '退款ID',
      dataIndex: 'id',
      key: 'id',
      width: 120,
      render: (text: string) => (
        <Button type="link">
          #{text.substring(0, 8)}
        </Button>
      ),
    },
    {
      title: '原支付ID',
      dataIndex: 'payment_intent_id',
      key: 'payment_intent_id',
      width: 120,
      render: (text: string) => (
        <Button type="link" size="small">
          #{text.substring(0, 8)}
        </Button>
      ),
    },
    {
      title: '退款金额',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      render: (amount: number, record: Refund) => (
        <Text strong>
          {record.currency} {(amount / 100).toFixed(2)}
        </Text>
      ),
    },
    {
      title: '退款原因',
      dataIndex: 'reason',
      key: 'reason',
      width: 150,
      render: (reason: string) => {
        const reasonMap = {
          'duplicate': '重复支付',
          'fraudulent': '欺诈交易',
          'requested_by_customer': '客户申请',
          'other': '其他原因',
        };
        return reasonMap[reason as keyof typeof reasonMap] || reason;
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => {
        const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
        return (
          <Tag color={config.color} icon={config.icon}>
            {config.text}
          </Tag>
        );
      },
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date: string) => new Date(date).toLocaleString(),
    },
    {
      title: '操作',
      key: 'actions',
      width: 120,
      render: (_: any, record: Refund) => (
        <Button 
          size="small" 
          icon={<EyeOutlined />}
          onClick={() => {
            Modal.info({
              title: '退款详情',
              content: '退款详情功能开发中...',
              width: 600,
            });
          }}
        >
          详情
        </Button>
      ),
    },
  ];

  const getStatsData = () => {
    const payments = paymentIntents.data || [];
    const refundsData = refunds.data || [];
    
    const totalAmount = payments.reduce((sum, payment) => sum + (payment.amount || 0), 0) / 100;
    const successfulPayments = payments.filter(p => p.status === 'succeeded').length;
    const totalRefunds = refundsData.reduce((sum, refund) => sum + (refund.amount || 0), 0) / 100;
    const successRate = payments.length > 0 ? (successfulPayments / payments.length) * 100 : 0;

    return {
      totalAmount,
      totalPayments: payments.length,
      successfulPayments,
      totalRefunds,
      successRate,
    };
  };

  const statsData = getStatsData();

  return (
    <div className="payments-page">
      {/* 页面头部 */}
      <div className="page-header">
        <div className="header-content">
          <Title level={2}>支付管理</Title>
          <Text type="secondary">管理支付交易和退款</Text>
        </div>
        <div className="header-actions">
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => window.location.reload()}>
              刷新
            </Button>
            <Button icon={<ExportOutlined />}>
              导出
            </Button>
          </Space>
        </div>
      </div>

      {/* 统计卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="交易总额"
              value={statsData.totalAmount}
              prefix="¥"
              precision={2}
              valueStyle={{ color: '#2F6BFF' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="交易笔数"
              value={statsData.totalPayments}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="成功率"
              value={statsData.successRate}
              suffix="%"
              precision={1}
              valueStyle={{ color: '#2F6BFF' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="退款总额"
              value={statsData.totalRefunds}
              prefix="¥"
              precision={2}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 搜索表单 */}
      <Card style={{ marginBottom: 16 }}>
        <Form
          form={searchForm}
          layout="inline"
          onFinish={handleSearch}
          style={{ marginBottom: 0 }}
        >
          <Form.Item name="search" style={{ marginBottom: 0 }}>
            <Input
              placeholder="搜索支付ID、客户邮箱"
              prefix={<SearchOutlined />}
              style={{ width: 200 }}
            />
          </Form.Item>
          <Form.Item name="status" style={{ marginBottom: 0 }}>
            <Select placeholder="状态" style={{ width: 120 }} allowClear>
              <Select.Option value="pending">待处理</Select.Option>
              <Select.Option value="processing">处理中</Select.Option>
              <Select.Option value="succeeded">成功</Select.Option>
              <Select.Option value="failed">失败</Select.Option>
              <Select.Option value="cancelled">已取消</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="payment_method" style={{ marginBottom: 0 }}>
            <Select placeholder="支付方式" style={{ width: 120 }} allowClear>
              <Select.Option value="card">银行卡</Select.Option>
              <Select.Option value="alipay">支付宝</Select.Option>
              <Select.Option value="wechat">微信支付</Select.Option>
              <Select.Option value="bank_transfer">银行转账</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="date_range" style={{ marginBottom: 0 }}>
            <RangePicker placeholder={['开始日期', '结束日期']} />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Space>
              <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                搜索
              </Button>
              <Button onClick={handleReset}>
                重置
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      {/* 主要内容 */}
      <Card>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane 
            tab={
              <Badge count={paymentIntents.data?.length || 0} offset={[10, 0]}>
                <span>支付记录</span>
              </Badge>
            } 
            key="payments"
          >
            <Table
              columns={paymentColumns}
              dataSource={paymentIntents.data || []}
              rowKey="id"
              loading={paymentIntents.loading}
              locale={{ emptyText: '暂无数据' }}
              pagination={{
                current: paymentIntents.pagination?.current_page || 1,
                pageSize: paymentIntents.pagination?.per_page || 20,
                total: paymentIntents.pagination?.total || 0,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `共 ${total} 条记录`,
              }}
              scroll={{ x: 1000 }}
            />
          </TabPane>
          
          <TabPane 
            tab={
              <Badge count={refunds.data?.length || 0} offset={[10, 0]}>
                <span>退款记录</span>
              </Badge>
            } 
            key="refunds"
          >
            <Table
              columns={refundColumns}
              dataSource={refunds.data || []}
              rowKey="id"
              loading={refunds.loading}
              locale={{ emptyText: '暂无数据' }}
              pagination={{
                current: refunds.pagination?.current_page || 1,
                pageSize: refunds.pagination?.per_page || 20,
                total: refunds.pagination?.total || 0,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `共 ${total} 条退款`,
              }}
              scroll={{ x: 1000 }}
            />
          </TabPane>
        </Tabs>
      </Card>

      {/* 支付详情 Modal (Stripe 风格分区) */}
      <Modal
        title="支付详情"
        open={detailVisible}
        onCancel={() => {
          setDetailVisible(false);
          setDetailTarget(null);
        }}
        footer={null}
        width={760}
      >
        {detailTarget && (() => {
          const p = detailTarget;
          const d = p.payment_method_details;
          const cur = p.currency;
          const sCfg = statusConfig[p.status as keyof typeof statusConfig] || statusConfig.pending;
          const risk = riskConfig[p.risk_level || ''] || null;
          const metaEntries = p.metadata
            ? Object.entries(p.metadata).filter(([k]) => k !== 'failure_reason')
            : [];
          const sectionStyle: React.CSSProperties = {
            marginTop: 8,
            marginBottom: 4,
            fontWeight: 600,
            fontSize: 14,
          };
          return (
            <div>
              {/* 概览 */}
              <div style={sectionStyle}>概览</div>
              <Descriptions bordered column={2} size="small">
                <Descriptions.Item label="金额">
                  <Text strong>{fmtAmount(p.amount, cur)}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="状态">
                  <Tag color={sCfg.color} icon={sCfg.icon}>
                    {sCfg.text}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="支付ID" span={2}>
                  <Text code copyable>{p.id}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="关联支付意图" span={2}>
                  {p.payment_intent_id ? <Text code>{p.payment_intent_id}</Text> : '—'}
                </Descriptions.Item>
                <Descriptions.Item label="描述" span={2}>
                  {p.description || '—'}
                </Descriptions.Item>
                <Descriptions.Item label="创建时间" span={2}>
                  {new Date(p.created_at).toLocaleString()}
                </Descriptions.Item>
              </Descriptions>

              {/* 金额拆解 */}
              <div style={sectionStyle}>金额拆解</div>
              <Descriptions bordered column={1} size="small">
                <Descriptions.Item label="总额">{fmtAmount(p.amount, cur)}</Descriptions.Item>
                <Descriptions.Item label="手续费">
                  {typeof p.fee === 'number' ? `-${fmtAmount(p.fee, cur)}` : '—'}
                </Descriptions.Item>
                <Descriptions.Item label="税费(代收)">
                  {typeof p.tax_amount === 'number' ? `-${fmtAmount(p.tax_amount, cur)}` : '—'}
                </Descriptions.Item>
                <Descriptions.Item label="商户净额">
                  <Text strong>
                    {typeof p.net_amount === 'number' ? fmtAmount(p.net_amount, cur) : '—'}
                  </Text>
                </Descriptions.Item>
              </Descriptions>

              {/* 支付方式 */}
              <div style={sectionStyle}>支付方式</div>
              <Descriptions bordered column={2} size="small">
                <Descriptions.Item label="类型">
                  {p.payment_method_type || d?.type || p.payment_method?.type || '—'}
                </Descriptions.Item>
                <Descriptions.Item label="卡品牌">
                  {d?.brand ? fmtCardBrand(d.brand) : '—'}
                </Descriptions.Item>
                <Descriptions.Item label="卡号">
                  {d?.last4 ? `•••• ${d.last4}` : '—'}
                </Descriptions.Item>
                <Descriptions.Item label="有效期">
                  {d?.exp_month && d?.exp_year
                    ? `${String(d.exp_month).padStart(2, '0')}/${d.exp_year}`
                    : '—'}
                </Descriptions.Item>
                <Descriptions.Item label="资金类型">
                  {d?.funding ? fmtFunding(d.funding) : '—'}
                </Descriptions.Item>
                <Descriptions.Item label="发卡国家/地区">
                  {d?.country || '—'}
                </Descriptions.Item>
                <Descriptions.Item label="持卡人" span={2}>
                  {d?.cardholder_name || '—'}
                </Descriptions.Item>
              </Descriptions>

              {/* 客户 */}
              <div style={sectionStyle}>客户</div>
              <Descriptions bordered column={2} size="small">
                <Descriptions.Item label="收据邮箱">
                  {p.receipt_email || p.customer?.email || '—'}
                </Descriptions.Item>
                <Descriptions.Item label="客户ID">
                  {p.customer_id ? <Text code>{p.customer_id}</Text> : '—'}
                </Descriptions.Item>
              </Descriptions>

              {/* 风控 */}
              <div style={sectionStyle}>风控</div>
              <Descriptions bordered column={2} size="small">
                <Descriptions.Item label="风险等级">
                  {risk ? <Tag color={risk.color}>{risk.text}</Tag> : (p.risk_level || '—')}
                </Descriptions.Item>
                <Descriptions.Item label="风险评分">
                  {typeof p.risk_score === 'number' ? p.risk_score : '—'}
                </Descriptions.Item>
              </Descriptions>

              {/* 元数据 */}
              {metaEntries.length > 0 && (
                <>
                  <div style={sectionStyle}>元数据</div>
                  <Descriptions bordered column={1} size="small">
                    {metaEntries.map(([k, v]) => (
                      <Descriptions.Item key={k} label={k}>
                        {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                      </Descriptions.Item>
                    ))}
                  </Descriptions>
                </>
              )}
            </div>
          );
        })()}
      </Modal>

      {/* 行内退款 Modal */}
      <Modal
        title="创建退款"
        open={refundModalVisible}
        onCancel={() => {
          setRefundModalVisible(false);
          setRefundTarget(null);
          refundForm.resetFields();
        }}
        footer={null}
        width={520}
      >
        <Form form={refundForm} layout="vertical" onFinish={handleSubmitRefund}>
          <Form.Item label="支付ID" name="payment_id">
            <Input disabled />
          </Form.Item>
          <Form.Item
            label={`退款金额（${refundTarget?.currency || 'USD'}，默认全额）`}
            name="amount"
            rules={[
              { required: true, message: '请输入退款金额' },
              {
                validator: (_, value) => {
                  const amount = parseFloat(value);
                  if (isNaN(amount) || amount <= 0) {
                    return Promise.reject('请输入有效的金额');
                  }
                  const max = (refundTarget?.amount || 0) / 100;
                  if (amount > max) {
                    return Promise.reject(`退款金额不能超过支付金额 ${max.toFixed(2)}`);
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber style={{ width: '100%' }} min={0.01} step={0.01} precision={2} />
          </Form.Item>
          <Form.Item
            label="退款原因"
            name="reason"
            rules={[{ required: true, message: '请选择退款原因' }]}
          >
            <Select placeholder="请选择">
              <Select.Option value="requested_by_customer">客户申请</Select.Option>
              <Select.Option value="duplicate">重复支付</Select.Option>
              <Select.Option value="fraudulent">欺诈交易</Select.Option>
              <Select.Option value="other">其他原因</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button
                onClick={() => {
                  setRefundModalVisible(false);
                  setRefundTarget(null);
                  refundForm.resetFields();
                }}
              >
                取消
              </Button>
              <Button type="primary" htmlType="submit" loading={refunding}>
                提交退款
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PaymentsPage; 